import React from "react";
import { Tooltip, IconButton } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const TPC_ACCOUNT_CENTER_URL = "https://myaccount.theportlandcompany.com";

// Build the admin dashboard deep link for a TPC income item. Proposals have
// their own editable dashboard page; subscriptions and invoices are managed on
// their client's page (there is no per-invoice/per-subscription route), so they
// deep-link to the client. Falls back to the public single-invoice page only
// when no better target is known.
function buildTpcHref(
  sourceDocumentType?: string,
  sourceDocumentId?: string,
  sourceClientId?: string
): string | null {
  if (!sourceDocumentId) return null;
  if (sourceDocumentType === "proposal") {
    return `${TPC_ACCOUNT_CENTER_URL}/dashboard/proposals/${sourceDocumentId}`;
  }
  if (
    (sourceDocumentType === "subscription" ||
      sourceDocumentType === "invoice") &&
    sourceClientId
  ) {
    return `${TPC_ACCOUNT_CENTER_URL}/dashboard/clients/${sourceClientId}`;
  }
  return `${TPC_ACCOUNT_CENTER_URL}/invoice/${sourceDocumentId}`;
}

/**
 * Small icon-link shown on income items imported from TPC Account Center.
 * Deep-links into the admin dashboard for that record (editable), not the
 * public read-only share page. Renders nothing for non-TPC income.
 */
export function TpcInvoiceLink({
  sourceProvider,
  sourceDocumentId,
  sourceDocumentType,
  sourceClientId,
  size = "small",
}: {
  sourceProvider?: string;
  sourceDocumentId?: string;
  sourceDocumentType?: string;
  sourceClientId?: string;
  size?: "small" | "medium";
}) {
  if (sourceProvider !== "tpc" || !sourceDocumentId) return null;
  const href = buildTpcHref(
    sourceDocumentType,
    sourceDocumentId,
    sourceClientId
  );
  if (!href) return null;
  const label =
    sourceDocumentType === "proposal"
      ? "Open proposal in TPC Account Center"
      : sourceDocumentType === "subscription"
        ? "Open subscription's client in TPC Account Center"
        : "Open in TPC Account Center";
  return (
    <Tooltip title="Manage in TPC Account Center" arrow placement="top">
      <IconButton
        size={size}
        component="a"
        href={href}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        aria-label={label}
        sx={{ color: "primary.main", p: 0.25 }}
      >
        <OpenInNewIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
}
